/**
 * Sign-in page. The only route reachable without a session.
 *
 * Offers "Sign in with Microsoft" when the backend has Entra ID configured,
 * and the email/password form unless the backend has turned it off. Until
 * `/api/auth/config` answers, the password form is shown so a backend without
 * the endpoint still has a working login.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, api } from '../api/client';
import type { AuthConfig } from '../api/types';
import { Button, ErrorNotice } from '../components/ui';
import { useAuth } from '../hooks/useAuth';

const SSO_ERRORS: Record<string, string> = {
  denied:
    'Microsoft sign-in was cancelled or your account has not been given access to FormaChat.',
  forbidden: 'That Microsoft account is not allowed to use FormaChat.',
  no_account:
    'Your Microsoft account has no FormaChat access yet. Ask an administrator to add you.',
  disabled_account: 'Your FormaChat account is disabled.',
  expired: 'The sign-in took too long. Please try again.',
  disabled: 'Microsoft sign-in is not configured.',
};

function MicrosoftLogo(): JSX.Element {
  return (
    <svg aria-hidden="true" viewBox="0 0 21 21" className="h-4 w-4">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function Login(): JSX.Element {
  const { login, ssoError } = useAuth();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(
    ssoError ? (SSO_ERRORS[ssoError] ?? 'Microsoft sign-in failed. Please try again.') : null,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.authConfig().then(setConfig, () => setConfig(null));
  }, []);

  const ssoEnabled = config?.sso_enabled ?? false;
  const passwordEnabled = config?.password_login_enabled ?? true;
  // With SSO on, the password form is a fallback tucked behind a link.
  const passwordVisible = passwordEnabled && (!ssoEnabled || showPassword);

  function handleSso(): void {
    setSubmitting(true);
    api.startSso(window.location.pathname + window.location.search);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (caught) {
      // Deliberately vague: a message distinguishing "no such user" from "wrong
      // password" tells an attacker which addresses are registered.
      setError(
        caught instanceof ApiError && caught.isUnauthenticated
          ? 'Incorrect email or password.'
          : 'Could not sign in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-lg font-semibold text-slate-900">Sign in to FormaChat</h1>
        <p className="mt-1 text-sm text-slate-500">
          {ssoEnabled
            ? 'Use your Formaspace Microsoft account.'
            : 'Use your Formaspace dashboard account.'}
        </p>

        {error && (
          <div className="mt-4">
            <ErrorNotice message={error} />
          </div>
        )}

        {ssoEnabled && (
          <Button
            variant="secondary"
            onClick={handleSso}
            disabled={submitting}
            className="mt-5 flex w-full items-center justify-center gap-2 py-2"
          >
            <MicrosoftLogo />
            Sign in with Microsoft
          </Button>
        )}

        {ssoEnabled && passwordEnabled && !showPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(true)}
            className="mt-4 w-full text-center text-xs text-slate-500 hover:text-slate-700"
          >
            Sign in with email and password instead
          </button>
        )}

        {passwordVisible && (
          <>
            {ssoEnabled && <hr className="mt-5 border-slate-200" />}

            <label
              className="mt-5 block text-sm font-medium text-slate-700"
              htmlFor="email"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue"
            />

            <label
              className="mt-4 block text-sm font-medium text-slate-700"
              htmlFor="password"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue"
            />

            <Button type="submit" disabled={submitting} className="mt-6 w-full py-2">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
